import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";

interface IntersectionMap {
  [key: string]: number;
}

interface VerticalAnchorListProps {
  children: React.ReactNode;
  indicators?: string[]; // 可选的指示器列表
  onChange?: (activeKey: string) => void;
}

export interface VerticalAnchorListRef {
  scrollToCard: (newActiveKey: string) => void;
}

const VerticalAnchorList = forwardRef<
  VerticalAnchorListRef,
  VerticalAnchorListProps
>(({ children, onChange }, ref) => {
  const [scrollKey, setScrollKey] = useState<string>("");
  const intersectionMap = useRef<IntersectionMap>({});
  const scrollingFlagRef = useRef(false);

  const scrollToCard = (newActiveKey: string) => {
    setScrollKey(newActiveKey);
    onChange?.(newActiveKey);
    scrollingFlagRef.current = true;
    setTimeout(() => {
      scrollingFlagRef.current = false;
      setScrollKey("");
    }, 500);
  };
  useImperativeHandle(ref, () => ({
    scrollToCard,
  }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const key = entry.target.getAttribute("data-key");
          if (key) {
            intersectionMap.current[key] = entry.intersectionRatio;
          }
        });

        if (!scrollingFlagRef.current) {
          const maxKey = Object.entries(intersectionMap.current).reduce(
            (max, [key, value]) =>
              value > intersectionMap.current[max] ? key : max,
            Object.keys(intersectionMap.current)[0]
          );

          if (maxKey) {
            onChange?.(maxKey);
          }
        }
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      }
    );

    const cards = document.querySelectorAll("[data-key]");
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [onChange]);

  // 克隆子元素并注入scrollToView属性
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        ...child.props,
        scrollToView: scrollKey === child.props["data-key"],
      });
    }
    return child;
  });

  return <>{childrenWithProps}</>;
});

export default VerticalAnchorList;
